/**
 * Enveloppe visuelle commune à tous les widgets (carte blanche, header optionnel).
 * En mode personnalisation : affiche les boutons modifier / supprimer sur chaque widget.
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  createContext,
  CSSProperties,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { GridStackContext } from "../../../lib/grid-stack-context";
import { GridStackWidgetContext } from "../../../lib/grid-stack-widget-context";
import { useDashboardEdit } from "../DashboardEditContext";
import {
  findSizeByDimensions,
  getWidgetCatalogEntry,
  WidgetTypeId,
} from "../widgetCatalog";
import { parseWidgetContent, setWidgetDraft } from "../widgetDraft";
import { DeleteWidgetButton } from "./DeleteWidgetButton";

export const WidgetPreviewContext = createContext(false);

type WidgetShellProps = {
  title?: string;
  /** Élément affiché à gauche du titre (ex. pastille de statut). */
  titleIcon?: ReactNode;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  removable?: boolean;
  /** Route de destination au clic (hors mode édition), définie par instance de widget */
  detailRoute?: string;
  /** "card" : carte blanche (défaut) ; "bare" : transparent, sans chrome (ex. titre de section). */
  variant?: "card" | "bare";
};

function isInteractiveElement(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('button, a, input, select, textarea, label, [role="button"]')
  );
}

export const WidgetShell = ({
  title,
  titleIcon,
  subtitle,
  action,
  children,
  className = "",
  removable = true,
  detailRoute,
  variant = "card",
}: WidgetShellProps) => {
  const navigate = useNavigate();
  const isPreview = useContext(WidgetPreviewContext);
  const { isEditing } = useDashboardEdit();
  const widgetContext = useContext(GridStackWidgetContext);
  const gridContext = useContext(GridStackContext);
  const showHeader = Boolean(title || subtitle || action);
  const widgetId = widgetContext?.widget.id;
  // `widget.type` n'est PAS renseigné (les widgets sont créés avec { id, content } seulement,
  // cf. Dashboard.tsx) : le type réel vit dans le `content` sérialisé, champ `name`.
  const widgetType = (widgetContext?.widget.type ??
    (widgetId &&
      parseWidgetContent(
        gridContext?._rawWidgetMetaMap.value.get(widgetId)?.content
      )?.name)) as WidgetTypeId | undefined;
  const showEditControls = isEditing && !isPreview && widgetId && gridContext;
  const isNavigable = Boolean(detailRoute && !isEditing && !isPreview);

  const handleNavigate = () => {
    if (detailRoute) {
      navigate(detailRoute);
    }
  };

  const handleShellClick = (event: React.MouseEvent) => {
    if (!isNavigable || isInteractiveElement(event.target)) return;
    handleNavigate();
  };

  const handleShellKeyDown = (event: React.KeyboardEvent) => {
    if (!isNavigable || isInteractiveElement(event.target)) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavigate();
    }
  };
  const isEditingCard = Boolean(isEditing && !isPreview);

  // Suppression : fondu léger de la carte, puis GridStack referme l'espace.
  const REMOVE_FADE_MS = 150;
  const [removing, setRemoving] = useState(false);
  const removeTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(removeTimer.current), []);

  const handleDelete = () => {
    if (!widgetId || !gridContext) return;
    setRemoving(true);
    removeTimer.current = setTimeout(
      () => gridContext.removeWidget(widgetId),
      REMOVE_FADE_MS
    );
  };

  // Pendant le fondu, on masque la poignée de resize (élément GridStack hors de la
  // carte, qui sinon resterait "dans le vide" le temps de la disparition).
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const item = rootRef.current?.closest(".grid-stack-item");
    if (item) item.classList.toggle("gs-item-deleting", removing);
  }, [removing]);

  // Animation de la carte : fondu rapide à la suppression, sinon état stable.
  const motionStyle: CSSProperties = removing
    ? {
        opacity: 0,
        pointerEvents: "none",
        transition: `opacity ${REMOVE_FADE_MS}ms ease`,
      }
    : { opacity: 1, transition: "opacity 200ms ease" };

  // Ouvre le configurateur pré-rempli avec la config du widget courant.
  // - "edit"      : modifie ce widget (editingWidgetId défini → mise à jour).
  // - "duplicate" : crée un NOUVEAU widget identique (pas d'editingWidgetId → ajout).
  // Duplication interdite si le type a atteint sa limite d'instances (ex. météo).
  const duplicateBlocked = (() => {
    if (!widgetType || !gridContext) return false;
    const entry = getWidgetCatalogEntry(widgetType);
    if (!entry || entry.maxInstances === undefined) return false;
    let count = 0;
    gridContext._rawWidgetMetaMap.value.forEach((w) => {
      if (parseWidgetContent(w.content)?.name === widgetType) count += 1;
    });
    return count >= entry.maxInstances;
  })();

  const openConfigurator = (mode: "edit" | "duplicate") => {
    if (!widgetId || !gridContext) return;
    if (mode === "duplicate" && duplicateBlocked) return;

    const meta = gridContext._rawWidgetMetaMap.value.get(widgetId);
    const parsed = parseWidgetContent(meta?.content);
    if (!parsed) return;

    const resolvedType = (widgetType ?? parsed.name) as WidgetTypeId;
    const entry = getWidgetCatalogEntry(resolvedType);
    if (!entry) return;

    const w = meta?.w ?? 3;
    const h = meta?.h ?? 5;
    const size = findSizeByDimensions(entry, w, h);

    setWidgetDraft({
      widgetType: resolvedType,
      sizeId: size.id,
      config: parsed.props,
      editingWidgetId: mode === "edit" ? widgetId : undefined,
      // On conserve la taille exacte du widget (réédition / duplication à l'identique).
      w,
      h,
    });

    navigate(`/widgets/${resolvedType}/configure`);
  };

  const editButton = showEditControls ? (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        openConfigurator("edit");
      }}
      className="text-grey-medium hover:text-tertiary hover:bg-grey-light/60 transition-colors p-1.5 rounded-full"
      aria-label="Modifier le widget"
      title="Modifier le widget"
    >
      <FontAwesomeIcon icon="pen" className="h-3.5 w-3.5" />
    </button>
  ) : null;

  const duplicateButton =
    showEditControls && !duplicateBlocked ? (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          openConfigurator("duplicate");
        }}
        className="text-grey-medium hover:text-tertiary hover:bg-grey-light/60 transition-colors p-1.5 rounded-full"
        aria-label="Dupliquer le widget"
        title="Dupliquer le widget"
      >
        <FontAwesomeIcon icon="clone" className="h-3.5 w-3.5" />
      </button>
    ) : null;

  const removeButton =
    showEditControls && removable ? (
      <DeleteWidgetButton onDelete={handleDelete} />
    ) : null;

  const editControls = (editButton || duplicateButton || removeButton) && (
    <div className="flex items-center gap-0.5">
      {editButton}
      {duplicateButton}
      {removeButton}
    </div>
  );

  return (
    <div
      onClick={isNavigable ? handleShellClick : undefined}
      onKeyDown={isNavigable ? handleShellKeyDown : undefined}
      tabIndex={isNavigable ? 0 : undefined}
      aria-label={
        isNavigable && widgetType
          ? `Ouvrir ${getWidgetCatalogEntry(widgetType)?.label ?? "le widget"}`
          : undefined
      }
      ref={rootRef}
      className={`group relative w-full h-full flex flex-col overflow-hidden transition-all duration-200 ${
        variant === "bare"
          ? isEditingCard
            ? "rounded-lg border border-dashed border-secondary/50"
            : isNavigable
              ? "cursor-pointer hover:border-secondary/60 hover:shadow-md transition-[border-color,box-shadow]"
              : ""
          : `bg-white rounded-xl border ${
              isEditingCard
                ? "border-secondary/40 ring-4 ring-secondary/15"
                : isNavigable
                  ? "border-grey-light cursor-pointer hover:border-secondary/60 hover:shadow-md transition-[border-color,box-shadow]"
                  : "border-grey-light"
            }`
      } ${className}`}
      // Carte : élévation marquée (style inline = appliqué à coup sûr) pour bien
      // ressortir. Variant "bare" : pas d'ombre (transparent). + anim de sortie.
      style={{
        ...(variant === "bare"
          ? {}
          : isEditingCard
            ? { boxShadow: "0 8px 24px -4px rgba(41, 54, 16, 0.25)" }
            : {
                boxShadow:
                  "0 1px 2px rgba(16, 24, 40, 0.06), 0 12px 28px -6px rgba(16, 24, 40, 0.22)",
              }),
        ...motionStyle,
      }}
    >
      {showHeader ? (
        <header className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 shrink-0">
          <div className="min-w-0">
            {title && (
              <div className="flex items-center gap-2 min-w-0">
                {titleIcon}
                <h3 className="font-oxaniumMedium text-default-text text-base truncate">
                  {title}
                </h3>
              </div>
            )}
            {subtitle && (
              <p className="text-xs text-grey-medium mt-0.5">{subtitle}</p>
            )}
          </div>
          {(action || editControls) && (
            <div
              className="flex items-center gap-1 shrink-0"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {action}
              {editControls}
            </div>
          )}
        </header>
      ) : (
        editControls && (
          <div
            className={`absolute top-2 right-2 z-20 ${
              isEditing ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            } transition-opacity bg-white/80 backdrop-blur-sm rounded-full shadow-md px-0.5`}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {editControls}
          </div>
        )
      )}
      <div className="flex-1 min-h-0 overflow-auto px-4 pb-4">{children}</div>
    </div>
  );
};
